import * as React from "react";

// MUI
import {
	withStyles,
	WithStyles,
	createStyles,
	Theme,
} from "@material-ui/core/styles";
import { Typography, Grid, Avatar } from "@material-ui/core";

// NPM
import clsx from "clsx";
import { animated } from "react-spring";

// Classes
import Utility from "../../domain/classes/Utility";
import Doctor from "../../domain/classes/Doctor";

// Components
import { AnimatedDeliveryMethodToolbar } from "./DeliveryMethodToolbar.Component";

// Interfaces
import IAppointmentRecord from "../../domain/interfaces/IAppointmentRecord";
import IDoctor from "../../domain/interfaces/IDoctor";

// Contexts
import { useApplicationManager } from "../../contexts/ApplicationManager.Context";
import { useDoctorsManager } from "../../contexts/DoctorsManager.Context";

// Classes
import AvailableSlot from "../../domain/classes/AvailableSlot";
import IAvailableSlot from "../../domain/interfaces/IAvailableSlot";

export const styles = (theme: Theme) =>
	createStyles({
		root: {
			display: "flex",
		},
		avatarContainer: {
			marginRight: theme.spacing(1),
			display: "flex",
			alignItems: "center",
		},
		avatar: {
			width: theme.spacing(8),
			height: theme.spacing(8),
		},
	});

export interface DoctorSummaryHeaderProps
	extends React.HTMLAttributes<HTMLDivElement> {}

type ConditionalProps =
	| {
			useSelected: true;
			data?: IAppointmentRecord;
			slots?: IAvailableSlot[];
	  }
	| {
			useSelected?: false;
			data: IAppointmentRecord;
			slots: IAvailableSlot[];
	  };

const DoctorSummaryHeader = React.forwardRef<
	HTMLDivElement,
	DoctorSummaryHeaderProps & WithStyles<typeof styles> & ConditionalProps
>(({ className, classes, data, useSelected, slots }, ref) => {
	// const { doctors, selectedDoctor } = useDoctorManager();
	const { selectedDoctor } = useApplicationManager();
	const { doctors } = useDoctorsManager();

	const [doctor, setDoctor] = React.useState<IDoctor>();

	const getCurrentDoctor = React.useCallback(() => {
		const dr = Doctor.getDoctorByBPId(
			(data as IAppointmentRecord).userId.toString(),
			doctors
		);
		setDoctor(dr);
	}, [data, doctors]);

	React.useEffect(() => {
		if (!useSelected) {
			getCurrentDoctor();
		}
	}, [getCurrentDoctor, useSelected]);

	return (
		<div className={clsx(classes.root, className)} ref={ref}>
			<div className={classes.avatarContainer}>
				<Avatar
					src={
						useSelected && selectedDoctor
							? selectedDoctor.ProfilePhotoUri
							: data && doctor
							? doctor.ProfilePhotoUri
							: ""
					}
					className={classes.avatar}
				/>
			</div>
			<Grid container spacing={1}>
				<Grid item xs={12}>
					<Typography variant="body1" display="inline">
						Dr&nbsp;
						{useSelected && selectedDoctor
							? selectedDoctor.Firstname
							: data && doctor
							? doctor.Firstname
							: "Stephen"}
					</Typography>
					&nbsp;
					<Typography
						variant="body1"
						color="primary"
						display="inline"
					>
						<strong>
							{useSelected && selectedDoctor
								? selectedDoctor.Lastname
								: data && doctor
								? doctor.Lastname
								: "Strange"}
						</strong>
					</Typography>
				</Grid>
				{doctor && data && (
					<Grid item xs={12}>
						<AnimatedDeliveryMethodToolbar
							doctor={doctor}
							data={data}
						/>
					</Grid>
				)}

				{!useSelected && data && slots && slots.length > 0 && (
					<Grid item xs={12}>
						<Typography variant="caption" display="inline">
							{AvailableSlot.generateTimeAvailability(slots)}
						</Typography>
					</Grid>
				)}
			</Grid>
		</div>
	);
});

const StyledDoctorSummaryHeader = withStyles(styles, {
	classNamePrefix: Utility.generateClassName("DoctorSummaryHeader"),
})(DoctorSummaryHeader);

export default StyledDoctorSummaryHeader;

export const AnimatedDoctorSummaryHeader = animated(StyledDoctorSummaryHeader);
